import type { TrailDefinition, TrailContext } from './types'

interface Bolt {
  segments: { x: number, y: number }[]
  life: number
  maxLife: number
  hue: number
}

interface LightningState {
  bolts: Bolt[]
  lastX: number
  lastY: number
  spawnTimer: number
}

const MAX_BOLTS = 8

function generateBoltSegments(x: number, y: number, length: number, angle: number, segments: number): { x: number, y: number }[] {
  const points: { x: number, y: number }[] = [{ x, y }]
  const segLen = length / segments

  let cx = x
  let cy = y
  for (let i = 0; i < segments; i++) {
    // Main direction + random jitter
    const jitter = (Math.random() - 0.5) * segLen * 1.4
    const perpAngle = angle + Math.PI / 2
    cx += Math.cos(angle) * segLen + Math.cos(perpAngle) * jitter
    cy += Math.sin(angle) * segLen + Math.sin(perpAngle) * jitter
    points.push({ x: cx, y: cy })
  }
  return points
}

export const lightning: TrailDefinition = {
  id: 'lightning',
  name: 'Lightning',

  init(): LightningState {
    return { bolts: [], lastX: 0, lastY: 0, spawnTimer: 0 }
  },

  update(state: unknown, ctx: TrailContext) {
    const s = state as LightningState
    const dt = ctx.deltaTime / 1000

    s.spawnTimer -= dt

    // Spawn bolts periodically near mouse
    if (ctx.mouseActive && s.spawnTimer <= 0 && s.bolts.length < MAX_BOLTS) {
      const boltCount = 1 + Math.floor(Math.random() * 2)
      for (let i = 0; i < boltCount; i++) {
        const angle = Math.random() * Math.PI * 2
        const length = 30 + Math.random() * 50
        s.bolts.push({
          segments: generateBoltSegments(ctx.mouseX, ctx.mouseY, length, angle, 5 + Math.floor(Math.random() * 4)),
          life: 1,
          maxLife: 0.1 + Math.random() * 0.15,
          hue: 200 + Math.random() * 40
        })
      }
      s.spawnTimer = 0.03 + Math.random() * 0.06
      s.lastX = ctx.mouseX
      s.lastY = ctx.mouseY
    }

    // Age bolts
    for (let i = s.bolts.length - 1; i >= 0; i--) {
      s.bolts[i].life -= dt / s.bolts[i].maxLife
      if (s.bolts[i].life <= 0) {
        s.bolts.splice(i, 1)
      }
    }
  },

  draw(state: unknown, ctx: TrailContext) {
    const s = state as LightningState
    const c = ctx.ctx

    for (const bolt of s.bolts) {
      if (bolt.segments.length < 2) continue
      const alpha = Math.max(0, bolt.life)

      // Multiple glow passes
      const passes = [
        { width: 6, a: alpha * 0.1 },
        { width: 3, a: alpha * 0.3 },
        { width: 1.5, a: alpha * 0.8 },
        { width: 0.5, a: alpha }
      ]

      for (const pass of passes) {
        c.beginPath()
        c.moveTo(bolt.segments[0].x, bolt.segments[0].y)
        for (let i = 1; i < bolt.segments.length; i++) {
          c.lineTo(bolt.segments[i].x, bolt.segments[i].y)
        }
        c.strokeStyle = `hsla(${bolt.hue}, 90%, 75%, ${pass.a})`
        c.lineWidth = pass.width
        c.lineCap = 'round'
        c.lineJoin = 'round'
        c.stroke()
      }

      // Bright flash at origin
      const origin = bolt.segments[0]
      const flashGrad = c.createRadialGradient(origin.x, origin.y, 0, origin.x, origin.y, 12)
      flashGrad.addColorStop(0, `hsla(${bolt.hue}, 80%, 90%, ${alpha * 0.4})`)
      flashGrad.addColorStop(1, 'transparent')
      c.fillStyle = flashGrad
      c.fillRect(origin.x - 12, origin.y - 12, 24, 24)
    }
  }
}
